// src/lib/etwSnapshot.ts
// ETW-Snapshot: Grundeingaben + Spielwiese, fuer Speichern/Wiederoeffnen und
// JSON-Export/Import (gleiches Format fuer beide, repariert den bisherigen
// Rundweg -- siehe PHASE-0-BEFUND.md, Punkt 5). Referenzimplementierung fuer
// die anderen 4 Analyzer (Phase 1, nach Abnahme von ETW).
//
// Bewusst ohne Import aus anderen src/lib-Dateien: diese Datei wird direkt per
// "node --experimental-strip-types" getestet (tests/etw-snapshot.test.mjs), nicht
// nur ueber Vite gebuendelt. Ein Cross-File-Import zwischen zwei src/lib/*.ts-Dateien
// braucht fuer beide Welten (tsc mit moduleResolution "bundler" ohne
// allowImportingTsExtensions, UND rohes Node ohne Bundler) unterschiedliche
// Datei-Endungen -- bis dafuer eine Konvention entschieden ist, bleibt diese
// Datei absichtlich in sich geschlossen. Kaufnebenkosten/Bundesland sind bewusst
// NICHT Teil von EtwSnapshotInput: die hat bereits ihr eigenes, versioniertes
// Subsystem (src/lib/purchaseCosts.ts, exportPurchaseCosts/importPurchaseCosts),
// das unveraendert zustaendig bleibt (siehe Eigentumswohnung.tsx).

export type AnalyzerTypeId = "etw" | "mfh" | "efh" | "gewerbe" | "mixeduse";

/**
 * input: die Grundeingaben (Kaufpreis, Finanzierung, Kosten ...).
 * playground: die "Spielwiese"-Anpassungen (Preis-/Miet-Slider), bewusst getrennt
 * von input, weil ein spaeteres Ziel-Szenario nur auf diesem Teil aufsetzen soll.
 * confirmedFields: Phase-4 Runde 2b, Baustein 2 -- Feldnamen (aus TInput), die aus einer
 * Pauschale/einem Default stammen UND vom Nutzer im neuen "Annahmen pruefen"-Formular per
 * "Passt so"-Haken bestaetigt wurden ("Annahmen-pruefen"-Fortschritt, Punkt 1/2 der
 * Vorgabe). Optional/abwaertskompatibel: alte Snapshots ohne dieses Feld gelten als
 * "nichts bestaetigt" (leeres Array), kein Format-Bruch.
 */
export type DealSnapshotEnvelope<TInput, TPlayground> = {
  version: number;
  analyzerType: AnalyzerTypeId;
  input: TInput;
  playground: TPlayground;
  confirmedFields: string[];
};

/** Liest ein Feld aus einem rohen (ggf. aelteren oder fremden) Objekt, sonst den Default. */
function resolveField<T>(raw: Record<string, unknown> | undefined, key: string, fallback: T): T {
  const value = raw?.[key];
  if (value === undefined || value === null) return fallback;
  if (typeof fallback === "number") return typeof value === "number" && Number.isFinite(value) ? (value as T) : fallback;
  if (typeof fallback === "boolean") return typeof value === "boolean" ? (value as T) : fallback;
  if (typeof fallback === "string") return typeof value === "string" ? (value as T) : fallback;
  return (value as T) ?? fallback;
}

/** Liest ein verschachteltes Objektfeld robust aus (leeres Objekt statt undefined/null). */
function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

/** Liest confirmedFields robust ein: alles, was kein Array aus Strings ist (fehlt, falscher
 *  Typ, alter Snapshot), gilt als "nichts bestaetigt". */
function resolveConfirmedFields(raw: Record<string, unknown> | undefined, key: string): string[] {
  const value = raw?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

export const ETW_SNAPSHOT_VERSION = 1;

export type EtwSnapshotInput = {
  kaufpreis: number;
  /** Fuer die Lage-Komponente des Deal-Score (src/core/score/dealScore.ts). Leerstring = keine PLZ. */
  plz: string;
  flaecheM2: number;
  mieteProM2Monat: number;
  leerstandPct: number;
  opexPctBrutto: number;
  instandhaltungPct: number;
  /** Bewirtschaftung (Baustein 5): ETW behaelt sein Hausgeld+Instandhaltungspct-Modell
   *  unveraendert -- kein Pauschal/Einzeln-Umschalter. Optionales, zusaetzliches Feld fuer
   *  die Ruecklage am Sondereigentum (die eigene Wohnung, nicht das Gemeinschaftseigentum,
   *  dessen Instandhaltung schon im Hausgeld/opexPctBrutto steckt) -- Default 0.
   *  Immer sichtbar (kein Modus-Flag noetig, anders als die anderen 4 Typen). */
  sondereigentumRuecklage: number;
  nkRenovierung: number;
  nkSanierung: number;
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026, Dominiks Vorgabe): Eigenkapital in Euro
   *  statt Beleihungsquote (LTV) -- wie MFH/EFH/Gewerbe/Gemischt. Kredit = Kaufpreis −
   *  max(0, EK − NK) -- ETW finanzierte vorher als einziger Typ All-in x LTV. */
  eigenkapital: number;
  /** Migrations-Bruecke: bei einem alten Snapshot/Export (vor der Umstellung auf
   *  eigenkapital) der frueher gespeicherte ltvPct-Wert, sonst null. Aufgeloest in
   *  core/score/etwDealScore.ts' resolveEtwEigenkapital(), sobald die Nebenkosten
   *  bekannt sind (eigenes Subsystem, hier nicht verfuegbar). */
  legacyLtvPct: number | null;
  zinsPct: number;
  tilgungPct: number;
  mietSteigerung: number;
  kostenSteigerung: number;
  capRatePct: number;
  /** Deal-Score v2 Leerstand-Obergrenze. Alte Snapshots ohne dieses Feld gelten als "vollstaendig". */
  vermietet: "vollstaendig" | "teilweise" | "nein";
};

export type EtwSnapshotPlayground = {
  priceAdjPct: number;
  rentAdjPct: number;
  applyAdjustments: boolean;
};

export type EtwSnapshot = DealSnapshotEnvelope<EtwSnapshotInput, EtwSnapshotPlayground>;

// UI-Defaults, siehe Eigentumswohnung.tsx (Beispielobjekt) -- gleiche Werte,
// damit ein unvollstaendiger Snapshot (Altzeile, nur Ergebniswerte) genauso
// aussieht wie ein frischer Analyzer-Start.
export const ETW_INPUT_DEFAULTS: EtwSnapshotInput = {
  kaufpreis: 350_000,
  plz: "",
  flaecheM2: 70,
  mieteProM2Monat: 12,
  leerstandPct: 0.03,
  opexPctBrutto: 0.25,
  instandhaltungPct: 0.01,
  sondereigentumRuecklage: 0,
  nkRenovierung: 0,
  nkSanierung: 0,
  financingOn: true,
  eigenkapital: 270_000,
  legacyLtvPct: null,
  zinsPct: 0.035,
  tilgungPct: 0.02,
  mietSteigerung: 0.01,
  kostenSteigerung: 0.015,
  capRatePct: 0.045,
  vermietet: "vollstaendig",
};

export const ETW_PLAYGROUND_DEFAULTS: EtwSnapshotPlayground = {
  priceAdjPct: 0,
  rentAdjPct: 0,
  applyAdjustments: true,
};

export function buildEtwSnapshot(
  input: EtwSnapshotInput,
  playground: EtwSnapshotPlayground,
  confirmedFields: string[] = []
): EtwSnapshot {
  return { version: ETW_SNAPSHOT_VERSION, analyzerType: "etw", input, playground, confirmedFields };
}

export type ResolvedEtwSnapshot = {
  input: EtwSnapshotInput;
  playground: EtwSnapshotPlayground;
  /** false, wenn die Kern-Eingaben (Kaufpreis/Flaeche/Miete) fehlen -- Altzeile oder leerer Import. */
  complete: boolean;
  /** Siehe DealSnapshotEnvelope-Dateikommentar -- [] bei alten Snapshots ohne dieses Feld. */
  confirmedFields: string[];
};

/**
 * Liest einen rohen Snapshot robust ein: jede bisherige Version, ein alter
 * JSON-Export (Spielwiese noch flach in input statt in playground), oder gar
 * keiner (Altzeile aus portfolio_objects mit nur Ergebniswerten in data).
 */
export function resolveEtwSnapshot(raw: unknown): ResolvedEtwSnapshot {
  const r = asRecord(raw);
  const rawInput = asRecord(r.input);
  const rawPlayground = asRecord(r.playground);

  const complete =
    typeof rawInput.kaufpreis === "number" &&
    typeof rawInput.flaecheM2 === "number" &&
    typeof rawInput.mieteProM2Monat === "number";

  const input: EtwSnapshotInput = {
    kaufpreis: resolveField(rawInput, "kaufpreis", ETW_INPUT_DEFAULTS.kaufpreis),
    plz: resolveField(rawInput, "plz", ETW_INPUT_DEFAULTS.plz),
    flaecheM2: resolveField(rawInput, "flaecheM2", ETW_INPUT_DEFAULTS.flaecheM2),
    mieteProM2Monat: resolveField(rawInput, "mieteProM2Monat", ETW_INPUT_DEFAULTS.mieteProM2Monat),
    leerstandPct: resolveField(rawInput, "leerstandPct", ETW_INPUT_DEFAULTS.leerstandPct),
    opexPctBrutto: resolveField(rawInput, "opexPctBrutto", ETW_INPUT_DEFAULTS.opexPctBrutto),
    instandhaltungPct: resolveField(rawInput, "instandhaltungPct", ETW_INPUT_DEFAULTS.instandhaltungPct),
    sondereigentumRuecklage: resolveField(rawInput, "sondereigentumRuecklage", ETW_INPUT_DEFAULTS.sondereigentumRuecklage),
    nkRenovierung: resolveField(rawInput, "nkRenovierung", ETW_INPUT_DEFAULTS.nkRenovierung),
    nkSanierung: resolveField(rawInput, "nkSanierung", ETW_INPUT_DEFAULTS.nkSanierung),
    financingOn: resolveField(rawInput, "financingOn", ETW_INPUT_DEFAULTS.financingOn),
    eigenkapital: resolveField(rawInput, "eigenkapital", ETW_INPUT_DEFAULTS.eigenkapital),
    legacyLtvPct:
      (rawInput?.eigenkapital === undefined || rawInput?.eigenkapital === null) &&
      typeof rawInput?.ltvPct === "number" && Number.isFinite(rawInput.ltvPct)
        ? (rawInput.ltvPct as number)
        : null,
    zinsPct: resolveField(rawInput, "zinsPct", ETW_INPUT_DEFAULTS.zinsPct),
    tilgungPct: resolveField(rawInput, "tilgungPct", ETW_INPUT_DEFAULTS.tilgungPct),
    mietSteigerung: resolveField(rawInput, "mietSteigerung", ETW_INPUT_DEFAULTS.mietSteigerung),
    kostenSteigerung: resolveField(rawInput, "kostenSteigerung", ETW_INPUT_DEFAULTS.kostenSteigerung),
    capRatePct: resolveField(rawInput, "capRatePct", ETW_INPUT_DEFAULTS.capRatePct),
    vermietet:
      rawInput.vermietet === "teilweise" || rawInput.vermietet === "nein"
        ? rawInput.vermietet
        : "vollstaendig",
  };

  // Spielwiese: zuerst aus playground lesen (neues Format), sonst aus input
  // (alte Exporte vor der Trennung Grundeingaben/Spielwiese hatten diese Felder
  // flach in input), sonst Default.
  const playground: EtwSnapshotPlayground = {
    priceAdjPct: resolveField(rawPlayground, "priceAdjPct", resolveField(rawInput, "priceAdjPct", ETW_PLAYGROUND_DEFAULTS.priceAdjPct)),
    rentAdjPct: resolveField(rawPlayground, "rentAdjPct", resolveField(rawInput, "rentAdjPct", ETW_PLAYGROUND_DEFAULTS.rentAdjPct)),
    applyAdjustments: resolveField(rawPlayground, "applyAdjustments", resolveField(rawInput, "applyAdjustments", ETW_PLAYGROUND_DEFAULTS.applyAdjustments)),
  };

  const confirmedFields = resolveConfirmedFields(r, "confirmedFields");

  return { input, playground, complete, confirmedFields };
}
